/* eslint-disable no-underscore-dangle */
/* eslint-disable react/prop-types */
import React, { useState, useRef, useEffect, useCallback } from "react";
import { Line } from "react-chartjs-2";
import Chart from "chart.js";
import annotation from "chartjs-plugin-annotation";
import { nFormatter } from "lib/utils";
import { useMediaQuery, Typography, makeStyles } from "@material-ui/core";
import moment from "moment";
import CustomTooltip from "app/common/chart/CustomTooltipComponent";
import _ from "lodash";
import { BsArrowUp } from "react-icons/bs";

Chart.plugins.register(annotation);

const useStyles = makeStyles(() => ({
  containerRow: {
    fontSize: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
  },
  containerDataRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "200px",
  },
}));

function DemandChartComponent({
  chartData,
  groupBy,
  filteredAggregatedHistorics,
}) {
  const chartRef = useRef(null);
  const classes = useStyles();
  const [tooltipProps, setTooltipProps] = useState({});
  const [annotationLine, setAnnotationLine] = useState({});
  const [selectedDatasetIndex, setSelectedDatasetIndex] = useState(null);
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("md"));

  const setPositionAndData = (payload) => {
    setTooltipProps({
      top: payload.top,
      left: payload.left,
      position: "fixed",
      tooltipModel: payload.tooltipModel,
      showTooltip: true,
      width: 250,
      isDemandPlanning: true,
    });
  };

  const hide = () => {
    setTooltipProps({
      ...tooltipProps,
      showTooltip: false,
    });
  };

  const getHistoricValue = (
    historicData,
    historicDate,
    valueNumber,
    typeDate,
    typeDateDiff = ""
  ) => {
    const formatDate = moment(historicDate, "MMM'YY")
      .add(14, "days")
      .format("YYYY-MM-DD");
    const historicValue = _.find(historicData, (item) => {
      if (item.date) {
        return (
          moment(item.date).format("MMM'YY") ===
          moment(formatDate)
            .subtract(valueNumber, typeDate)
            .format("MMM'YY")
        );
      }
      return (
        item.x ===
        moment(formatDate)
          .subtract(valueNumber, typeDate)
          .format("MMM'YY")
      );
    });

    const specificvalue = historicValue ? "mid_units" : undefined;

    return typeDate === "years" || typeDateDiff === "years"
      ? historicValue && historicValue[specificvalue]
      : historicValue?.y;
  };

  const calculateYoYAgreggated = (historicDate, label, dataPoint) => {
    if (dataPoint.index >= 15) {
      return getHistoricValue(
        chartData.datasets[1].data,
        historicDate,
        1,
        "years",
        label,
        dataPoint.index
      );
    }
    return getHistoricValue(
      filteredAggregatedHistorics,
      historicDate,
      1,
      "years",
      label,
      dataPoint.index
    );
  };

  const calculateYoYGroupBy = (historicDate, label, dataPoint) => {
    if (dataPoint.index >= 15) {
      return getHistoricValue(
        chartData.datasets[dataPoint.datasetIndex].data,
        historicDate,
        1,
        "years",
        label,
        dataPoint.index
      );
    }
    if (
      chartData.datasets[dataPoint.datasetIndex].data[dataPoint.index].mid_units
    ) {
      return getHistoricValue(
        filteredAggregatedHistorics[dataPoint.datasetIndex],
        historicDate,
        1,
        "years",
        label,
        dataPoint.index
      );
    }
    return undefined;
  };

  const getHistoricValueForYoY = (historicDate, label, dataPointIndex) => {
    return groupBy.key !== null
      ? calculateYoYGroupBy(historicDate, label, dataPointIndex)
      : calculateYoYAgreggated(historicDate, label, dataPointIndex);
  };

  const getDifferenceYoyOrMom = (value, historicValue) => {
    return _.round((100 * (value - historicValue)) / historicValue, 1);
  };

  const historicTitle = (index) => {
    if (index.low_units === undefined) {
      return `Historic Value - ${
        chartRef.current.chartInstance.data.datasets[
          tooltipProps.tooltipModel.dataPoints[0].datasetIndex
        ]?.data[tooltipProps.tooltipModel.dataPoints[0].index].x
      }`;
    }
    return `Probable Case - ${
      chartRef.current.chartInstance.data.datasets[
        tooltipProps.tooltipModel.dataPoints[0].datasetIndex
      ]?.data[tooltipProps.tooltipModel.dataPoints[0].index].x
    }`;
  };

  const chartOptions = {
    maintainAspectRatio: false,
    legend: {
      display: false,
    },
    layout: {
      padding: {
        left: 20,
        right: 40,
        top: 30,
        bottom: 30,
      },
    },
    tooltips: {
      enabled: false,
      intersect: isSmallScreen,
      mode: "index",
      custom: React.useCallback((tooltipModel) => {
        if (tooltipModel.opacity === 0) {
          hide();
          return;
        }
        const chart = chartRef.current.chartInstance;

        const { canvas } = chart.ctx;
        if (canvas) {
          const position = canvas.getBoundingClientRect();
          // set position of tooltip
          const left =
            tooltipModel.caretX <= chart.width / 2
              ? position.left + tooltipModel.caretX + 40
              : position.left + tooltipModel.caretX - 220;
          const top =
            tooltipModel.caretY <= chart.height / 2
              ? position.top + tooltipModel.caretY - 20
              : position.top + tooltipModel.caretY - 140;

          setPositionAndData({ top, left, tooltipModel });
        }
      }, []),
    },
    annotation: {
      annotations: [annotationLine],
    },
    scales: {
      yAxes: [
        {
          id: "Units",
          scaleLabel: {
            labelString: "Units",
            fontStyle: "bold",
            display: false,
          },
          gridLines: {
            drawOnChartArea: true,
            drawTicks: false,
            color: "#A9A9A9",
            lineWidth: 0.5,
          },
          ticks: {
            padding: 20,
            beginAtZero: false,
            autoSkip: true,
            maxTicksLimit: 8,
            maxRotation: 0,
            minRotation: 0,
            callback: React.useCallback((value) => {
              return `${nFormatter(value)}`;
            }, []),
          },
          position: "left",
        },
      ],
      xAxes: [
        {
          id: "Time",
          scaleLabel: {
            labelString: "Time",
            display: false,
            fontStyle: "bold",
          },
          gridLines: {
            drawOnChartArea: false,
            drawTicks: false,
            color: "#A9A9A9",
            lineWidth: 0.5,
          },
          ticks: {
            padding: 20,
            beginAtZero: false,
            autoSkip: true,
            maxTicksLimit: isSmallScreen ? 9 : 20,
            maxRotation: isSmallScreen ? 45 : 0,
            minRotation: 0,
            callback: (value) => value,
          },
          position: "bottom",
        },
      ],
    },
    onHover: useCallback((event, chartElement) => {
      const chart = chartRef.current.chartInstance;
      const datasetIndex = 0;

      if (!chartElement[0]) {
        setAnnotationLine({});
      } else {
        // eslint-disable-next-line no-underscore-dangle
        setSelectedDatasetIndex(chartElement[0]._datasetIndex);
        const activeTooltip = chart.tooltip._active
          ? chart.tooltip._active[datasetIndex]?._index
          : 0;
        if (isSmallScreen) {
          setAnnotationLine({});
        } else {
          setAnnotationLine({
            drawTime: "beforeDatasetsDraw",
            type: "line",
            mode: "vertical",
            borderColor: "rgba(166, 166, 166, 0.05)",
            borderWidth: 30,
            scaleID: "Time",
            value: `${chart.data.datasets[datasetIndex]?.data[activeTooltip]?.x}`,
          });
        }
      }
    }, []),
  };

  useEffect(() => {
    const chart = chartRef.current.chartInstance;
    if (
      groupBy.key === null ||
      chartRef.current.props.data.datasets.length === 1
    ) {
      chart.options.hover = {};
    } else {
      chart.options.hover = {
        mode: "dataset",
      };
    }
    chart.update();
    hide();
  }, [chartData]);

  return (
    <>
      {tooltipProps.showTooltip ? (
        <CustomTooltip
          tooltipProps={tooltipProps}
          items={_.map(tooltipProps.tooltipModel.dataPoints, (dataPoint) => {
            chartRef.current.chartInstance.data.datasets[
              dataPoint.datasetIndex
            ].borderDash = [4, 0];
            if (
              tooltipProps.tooltipModel.dataPoints.length > 5 &&
              dataPoint.datasetIndex !== selectedDatasetIndex
            )
              return null;
            return {
              content: (
                <div className={`${classes.containerDataRow}`}>
                  <div>
                    <Typography variant="body2">
                      <>
                        {(dataPoint.index === 0 ||
                          dataPoint.index === 1 ||
                          dataPoint.index === 2) &&
                        groupBy.key === null ? (
                          <> Historic Value </>
                        ) : (
                          <>
                            {
                              chartRef.current.chartInstance.data.datasets[
                                dataPoint.datasetIndex
                              ]?.data[dataPoint.index].label
                            }
                          </>
                        )}
                      </>
                    </Typography>
                    <Typography variant="caption">
                      {dataPoint.value
                        ? `${nFormatter(parseFloat(dataPoint.value))} units`
                        : ""}
                    </Typography>
                  </div>
                  {getHistoricValueForYoY(
                    dataPoint.label,
                    chartRef.current.chartInstance.data.datasets[
                      dataPoint.datasetIndex
                    ].label,
                    dataPoint
                  ) &&
                    getDifferenceYoyOrMom(
                      dataPoint.value,
                      getHistoricValueForYoY(
                        dataPoint.label,
                        chartRef.current.chartInstance.data.datasets[
                          dataPoint.datasetIndex
                        ].label,
                        dataPoint
                      )
                    ) !== 0 && (
                      <div className={`${classes.containerRow}`}>
                        <BsArrowUp
                          size={16}
                          strokeWidth={1.5}
                          style={{
                            marginRight: "5px",
                            color: `${
                              getDifferenceYoyOrMom(
                                dataPoint.value,
                                getHistoricValueForYoY(
                                  dataPoint.label,
                                  chartRef.current.chartInstance.data.datasets[
                                    dataPoint.datasetIndex
                                  ].label,
                                  dataPoint
                                )
                              ) > 0
                                ? "#5AB793"
                                : "#FD6464"
                            }`,
                            transform: `rotateX(${
                              getDifferenceYoyOrMom(
                                dataPoint.value,
                                getHistoricValueForYoY(
                                  dataPoint.label,
                                  chartRef.current.chartInstance.data.datasets[
                                    dataPoint.datasetIndex
                                  ].label,
                                  dataPoint
                                )
                              ) > 0
                                ? "0deg"
                                : "180deg"
                            }
                      )`,
                          }}
                        />
                        {`${Math.abs(
                          getDifferenceYoyOrMom(
                            dataPoint.value,
                            getHistoricValueForYoY(
                              dataPoint.label,
                              chartRef.current.chartInstance.data.datasets[
                                dataPoint.datasetIndex
                              ].label,
                              dataPoint
                            )
                          )
                        )}% YoY`}
                      </div>
                    )}
                </div>
              ),
              borderStyle: {
                border:
                  dataPoint.datasetIndex === selectedDatasetIndex &&
                  groupBy.key !== null &&
                  chartRef.current.props.data.datasets.length !== 1
                    ? `dashed 2px ${
                        chartRef.current.chartInstance.data.datasets[
                          dataPoint.datasetIndex
                        ]?.hoverBorderColor
                      }`
                    : `2px solid ${
                        chartRef.current.chartInstance.data.datasets[
                          dataPoint.datasetIndex
                        ]?.borderColor
                      }`,
              },
            };
          })}
          tooltipData={{
            title:
              groupBy.key === null
                ? `Sell Out - ${
                    chartRef.current.chartInstance.data.datasets[
                      tooltipProps.tooltipModel.dataPoints[0].datasetIndex
                    ]?.data[tooltipProps.tooltipModel.dataPoints[0].index].x
                  }`
                : historicTitle(
                    chartRef.current.chartInstance.data.datasets[
                      tooltipProps.tooltipModel.dataPoints[0].datasetIndex
                    ].data[tooltipProps.tooltipModel.dataPoints[0].index]
                  ),
          }}
        />
      ) : null}
      <Line ref={chartRef} data={chartData} options={chartOptions} />
    </>
  );
}

export default DemandChartComponent;
